<template>
  <v-row class="fill-height ma-0 pa-0" no-gutters>
    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar"
      top
      right
      color="error"
      timeout="4000"
    >
      <div class="d-flex align-center">
        <v-icon left>mdi-alert-circle</v-icon>
        <span>{{ snackbarMessage }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="snackbar = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Columna de imagen (600x960) -->
    <v-col 
      v-if="show" 
      :lg="cols[0]" 
      :md="5" 
      class="image-section pa-0"
    >
      <v-img 
        :src="`/img/login/img_${img}.png`" 
        class="fill-height"
        :aspect-ratio="600/960"
      />
    </v-col>

    <!-- Columna de formulario -->
    <v-col 
      :lg="cols[1]" 
      :md="7" 
      :sm="12"
      class="form-section d-flex align-center"
    >
      <v-container fluid class="pa-0">
        <v-row class="justify-center ma-0">
          <v-col cols="12" sm="10" md="9" lg="8" xl="6">
            
            <!-- Header con logos -->
            <div class="text-center mb-8">
              <v-avatar size="80" class="logo-main elevation-3 mb-6">
                <v-img src="/img/logo_ubga.jpg" />
              </v-avatar>
              <v-img 
                src="/img/logotipo-600x100.png" 
                height="50px" 
                contain 
                class="mb-8"
              />
            </div>

            <!-- Card principal -->
            <v-card class="login-card elevation-8">
              <v-card-text class="pa-8 pa-sm-12">
                <!-- Título -->
                <div class="mb-8">
                  <h1 class="text-h4 font-weight-bold mb-2 primary-text">
                    ¡Bienvenido!
                  </h1>
                  <p class="text-body-1 grey--text text--darken-1">
                    Ingresa a tu cuenta
                  </p>
                </div>

                <!-- Formulario -->
                <v-form ref="form" v-model="valid" @submit.prevent="login">
                  
                  <!-- Campo Usuario -->
                  <div class="mb-6">
                    <label class="field-label mb-2 d-block">Usuario</label>
                    <v-text-field
                      v-model="username"
                      :rules="[rules.required, rules.min6]"
                      placeholder="Tu nombre de usuario"
                      outlined
                      dense
                      hide-details="auto"
                      class="modern-input"
                      color="#A96419"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="#A96419" size="20">mdi-account</v-icon>
                      </template>
                    </v-text-field>
                  </div>

                  <!-- Campo Contraseña -->
                  <div class="mb-2">
                    <label class="field-label mb-2 d-block">Contraseña</label>
                    <v-text-field
                      v-model="password"
                      :rules="[rules.required, rules.min8]"
                      :type="show1 ? 'text' : 'password'"
                      placeholder="Tu contraseña"
                      outlined
                      dense
                      hide-details="auto"
                      class="modern-input"
                      color="#A96419"
                      @keydown.enter="login"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="#A96419" size="20">mdi-lock</v-icon>
                      </template>
                      <template v-slot:append>
                        <v-btn icon small @click="show1 = !show1" class="mr-n2">
                          <v-icon size="20" color="grey">
                            {{ show1 ? 'mdi-eye-off' : 'mdi-eye' }}
                          </v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </div>

                  <!-- Botón de login -->
                  <v-btn
                    block
                    x-large
                    class="primary-button text-capitalize font-weight-bold mb-4"
                    :loading="loading"
                    :disabled="!valid || loading"
                    @click="login"
                    elevation="0"
                  >
                    <v-icon left size="22">mdi-login</v-icon>
                    {{ loading ? 'Iniciando sesión...' : 'Iniciar Sesión' }}
                  </v-btn>
                </v-form>
              </v-card-text>
            </v-card>

            <!-- Footer -->
            <div class="text-center mt-8">
              <p class="text-caption grey--text text--lighten-1">
                © {{ currentYear }} UBGA. Todos los derechos reservados.
              </p>
            </div>

          </v-col>
        </v-row>
      </v-container>
    </v-col>
  </v-row>
</template>

<script>
export default {
  data() {
    return {
      show1: false,
      username: '',
      password: '',
      valid: false,
      loading: false,
      rules: {
        required: value => !!value || 'Este campo es requerido',
        min6: v => (v && v.length >= 6) || 'Mínimo 6 caracteres',
        min8: v => (v && v.length >= 8) || 'Mínimo 8 caracteres',
      },
      snackbar: false,
      snackbarMessage: '',
      img: 1,
    }
  },
  created() {
    this.img = Math.floor(Math.random() * 3) + 1;
  },
  methods: {
    async login() {
      if (!this.$refs.form.validate()) {
        this.snackbarMessage = 'Por favor completa todos los campos correctamente';
        this.snackbar = true;
        return;
      }

      this.loading = true;

      try {
        const response = await this.$store.dispatch('login', { 
          username: this.username, 
          password: this.password 
        });
        
        if (response === 'auth_success') {
          this.$router.push('/dashboard');
        }
      } catch (err) {
        console.error(err);
        this.snackbarMessage = 'Usuario y/o contraseña incorrectos';
        this.snackbar = true;
        
        const card = document.querySelector('.login-card');
        if (card) {
          card.classList.add('shake');
          setTimeout(() => card.classList.remove('shake'), 500);
        }
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    show() {
      return !this.$vuetify.breakpoint.xs && !this.$vuetify.breakpoint.sm;
    },
    cols() {
      if (this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm) {
        return [0, 12];
      }
      // Ajustado para la proporción 600x960 de la imagen
      return [5, 7];
    },
    currentYear() {
      return new Date().getFullYear();
    }
  },
}
</script>

<style scoped>
.fill-height {
  min-height: 100vh;
}

/* Sección de imagen ajustada a 600x960 */
.image-section {
  overflow: hidden;
  height: 100vh;
  background: #f5f5f5;
}

.image-section .v-image {
  height: 100% !important;
  width: 100%;
  object-fit: cover;
}

/* Sección de formulario */
.form-section {
  background: #FAFAFA;
  min-height: 100vh;
  padding: 32px 16px;
}

/* Logo */
.logo-main {
  background: white;
  border: 3px solid #f5f5f5;
  transition: transform 0.3s ease;
}

.logo-main:hover {
  transform: scale(1.05);
}

/* Card principal */
.login-card {
  background: white;
  border: 1px solid #f0f0f0;
  position: relative;
}

.login-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #A96419, #C87D2A, #A96419);
}

.shake {
  animation: shake 0.4s;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  75% { transform: translateX(10px); }
}

/* Textos */
.primary-text {
  color: #A96419;
}

.field-label {
  font-size: 14px;
  font-weight: 600;
  color: #424242;
}

/* Inputs */
.modern-input >>> .v-input__slot {
  background: #F8F9FA !important;
  border-radius: 12px !important;
  min-height: 52px !important;
}

.modern-input >>> .v-input__slot:hover {
  background: #FFFFFF !important;
}

.modern-input >>> fieldset {
  border-color: #E8E8E8 !important;
  border-width: 1.5px !important;
}

.modern-input >>> .v-input--is-focused fieldset {
  border-color: #A96419 !important;
  border-width: 2px !important;
}

/* Botón primario */
.primary-button {
  background: linear-gradient(135deg, #A96419 0%, #8B5216 100%) !important;
  color: white !important;
  font-size: 16px !important;
  height: 56px !important;
  transition: all 0.3s ease;
}

.primary-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(169, 100, 25, 0.35) !important;
}

.primary-button:active {
  transform: translateY(0);
}

.primary-button.v-btn--disabled {
  background: #E0E0E0 !important;
  color: #9E9E9E !important;
}

/* Responsive */
@media (max-width: 960px) {
  .form-section {
    padding: 24px 12px;
  }
}

@media (max-width: 600px) {
  .text-h4 {
    font-size: 1.75rem !important;
  }
  
  .login-card .v-card__text {
    padding: 32px 24px !important;
  }
  
  .logo-main {
    width: 70px !important;
    height: 70px !important;
  }
}
</style>